import { GridCellParams } from '@mui/x-data-grid-pro';
import { v4 } from 'uuid';
import { GridCustomColDef } from '../../types';
import {
  advancedOperators,
  advancedNumericOperators,
  advancedDatePickerOperator,
  runPickerOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { addDrawer } from '../../../store/Drawer';
import { Icon } from '../../Atoms/Icon';
import { Text } from '../../Atoms/Text';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { getRpmAccountById } from '../../../store/RpmAccount';
import { CustomerRpmAccount } from '../../../store/CustomerRpmAccount';
import { formatPrice, getDate, getFormattedDate, formatPercent } from '../../../utils/helpers';

const handleAssetClick = async (row: CustomerRpmAccount) => {
  const rpmAccount = await getRpmAccountById(String(row.id));
  addDrawer({
    id: v4(),
    type: 'rpmAccount',
    ...getMetaData({ name: ComponentName.RPM_ACCOUNT, id: String(row.id) }),
    data: rpmAccount
  });
};

export const CustomerRpmAccountColumnDefs = (tabIndex: number): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'accountNumber',
    headerName: 'Number',
    sortable: true,
    filterable: true,
    minWidth: 160,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedOperators(),
    quickFilter: true,
    pinned: 'left',
    renderCell: (params: GridCellParams<CustomerRpmAccount>) => {
      return (
        <button
          className="flex items-center"
          onClick={() => handleAssetClick(params.row)}>
          <Icon
            className="p-3 mr-1"
            type="rpmAccountMed"
          />
          <Text
            size="sm14"
            color="indigo">
            {params.value as string}
          </Text>
        </button>
      );
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'firstCommissionRun',
    headerName: 'First Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    minWidth: 90,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'lastCommissionRun',
    headerName: 'Last Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    minWidth: 90,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'chargeRecurringActual',
    headerName: 'Act. MRC',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value) => value,
    renderCell: (params: GridCellParams<CustomerRpmAccount>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'revenueRecurringActual',
    headerName: 'Act. MRR',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value) => value,
    renderCell: (params: GridCellParams<CustomerRpmAccount>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPrice(params.value as number)}
        </span>
      );
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'revenueRecurringActualPercent',
    headerName: 'Act. %',
    headerAlign: 'right',
    align: 'right',
    sortable: true,
    filterable: true,
    minWidth: 100,
    flex: 0.5,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    valueGetter: (value) => value,
    renderCell: (params: GridCellParams<CustomerRpmAccount>) => {
      if (params.value == null) return <span className="text-gray-600">—</span>;
      return (
        <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
          {formatPercent(params.value as number, true)}
        </span>
      );
    }
  },
  {
    field: 'rpmSupplierName',
    headerName: 'Supplier',
    sortable: true,
    filterable: true,
    flex: 1,
    minWidth: 150,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    field: 'rpmCustomerName',
    headerName: 'Customer',
    sortable: true,
    filterable: true,
    flex: 1,
    minWidth: 150,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    field: 'source',
    headerName: 'Source',
    sortable: true,
    filterable: true,
    flex: 1,
    minWidth: 150,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'addedDate',
    headerName: 'Added Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    minWidth: 130,
    flex: 1,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator(),
    hideByDefault: tabIndex !== 6
  },
  {
    field: 'linkCount',
    headerName: 'Links',
    sortable: true,
    filterable: true,
    flex: 0.6,
    minWidth: 90,
    type: 'number',
    filterOperators: advancedNumericOperators(),
    hideByDefault: tabIndex !== 5
  }
];
